/**
 * @quantbar/api-client — base HTTP layer
 *
 * Refactored from client/src/api/base.js.
 * All localStorage access has been replaced with StorageAdapter calls so this
 * module works in any platform (web, React Native, Tauri).
 *
 * Business logic (token key names, refresh flow) is unchanged.
 */

import type { StorageAdapter } from './StorageAdapter.js';

const TOKEN_KEYS = ['token', 'authToken'] as const;

export interface ApiClientConfig {
  baseUrl: string;
  storage: StorageAdapter;
  /**
   * Called whenever a request comes back with HTTP 401 against an
   * authenticated endpoint (i.e. `skipAuth: true` calls do NOT trigger this).
   * Use it to flip your auth store to a signed-out state and redirect to a
   * login screen — without this, every screen has to handle the "session
   * expired" string itself, which never works reliably in practice.
   */
  onUnauthorized?: () => void;
}

export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  skipAuth?: boolean;
  rawResponse?: boolean;
  body?: unknown;
  suppressErrorLogging?: boolean;
}

const API_DEBUG_LOGS =
  typeof process !== 'undefined' &&
  typeof process.env !== 'undefined' &&
  process.env.EXPO_PUBLIC_API_DEBUG === 'true';

function debugLog(...args: unknown[]) {
  if (API_DEBUG_LOGS) {
    console.log(...args);
  }
}

function debugWarn(...args: unknown[]) {
  if (API_DEBUG_LOGS) {
    console.warn(...args);
  }
}

function logApiError(suppressErrorLogging: boolean, ...args: unknown[]) {
  if (!suppressErrorLogging) {
    console.error(...args);
  }
}

export interface ApiError extends Error {
  status?: number;
  payload?: unknown;
  code?: string;
  isNetworkError?: boolean;
  isHtmlError?: boolean;
}

export function createBaseClient(config: ApiClientConfig) {
  const { baseUrl, storage, onUnauthorized } = config;
  const normalizedBaseUrl = baseUrl.replace(/\/+$/, '');

  async function getItem(key: string): Promise<string | null> {
    return storage.getItem(key);
  }

  async function setItem(key: string, value: string): Promise<void> {
    return storage.setItem(key, value);
  }

  async function removeItem(key: string): Promise<void> {
    return storage.removeItem(key);
  }

  async function getAuthToken(): Promise<string | null> {
    for (const key of TOKEN_KEYS) {
      const value = await getItem(key);
      if (value && value.trim()) {
        return value.trim();
      }
    }
    const refreshToken = await getItem('refreshToken');
    if (refreshToken && refreshToken.trim()) {
      debugWarn('[getAuthToken] Access token missing, but refresh token exists. Please log in again.');
    } else {
      debugWarn('[getAuthToken] No token found in storage. Keys checked:', TOKEN_KEYS);
    }
    return null;
  }

  async function clearAuthToken(): Promise<void> {
    for (const key of TOKEN_KEYS) {
      await removeItem(key);
    }
  }

  async function apiRequest<T = unknown>(
    endpoint: string,
    options: ApiRequestOptions = {},
  ): Promise<T> {
    debugLog('[apiRequest] Starting request:', endpoint, {
      method: options.method ?? 'GET',
      skipAuth: options.skipAuth,
    });

    const {
      skipAuth = false,
      rawResponse = false,
      body: rawBody,
      suppressErrorLogging = false,
      ...fetchOptions
    } = options;

    let token: string | null = skipAuth ? null : await getAuthToken();

    if (!skipAuth && !token) {
      const refreshToken = await getItem('refreshToken');
      if (refreshToken && typeof refreshToken === 'string' && refreshToken.trim()) {
        debugLog('[apiRequest] Attempting to refresh access token...');
        try {
          const refreshTokenValue = refreshToken.trim();
          if (!refreshTokenValue) throw new Error('Refresh token is empty');

          const refreshResponse = await fetch(`${normalizedBaseUrl}/api/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken: refreshTokenValue }),
          });

          if (refreshResponse.ok) {
            const refreshData = (await refreshResponse.json()) as {
              accessToken?: string;
              refreshToken?: string;
            };
            if (refreshData?.accessToken) {
              await setItem('token', refreshData.accessToken);
              await setItem('authToken', refreshData.accessToken);
              if (refreshData.refreshToken) {
                await setItem('refreshToken', refreshData.refreshToken);
              }
              token = refreshData.accessToken;
              debugLog('[apiRequest] Successfully refreshed access token');
            } else {
              logApiError(suppressErrorLogging, '[apiRequest] Refresh response missing accessToken');
              await clearAuthToken();
            }
          } else {
            logApiError(
              suppressErrorLogging,
              '[apiRequest] Failed to refresh token, status:',
              refreshResponse.status,
            );
            await clearAuthToken();
          }
        } catch (refreshError) {
          logApiError(suppressErrorLogging, '[apiRequest] Error refreshing token:', refreshError);
          await clearAuthToken();
        }
      }

      if (!token) {
        logApiError(suppressErrorLogging, '[apiRequest] No auth token available for request to:', endpoint);
      }
    }

    const url = (() => {
      if (!endpoint) return normalizedBaseUrl;
      if (/^https?:\/\//i.test(endpoint)) return endpoint;
      try {
        return new URL(endpoint, `${normalizedBaseUrl}/`).toString();
      } catch {
        const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
        return `${normalizedBaseUrl}${normalizedEndpoint}`;
      }
    })();

    let requestBody: string | undefined;
    if (rawBody !== undefined && rawBody !== null) {
      if (typeof rawBody === 'string') {
        requestBody = rawBody;
      } else if (typeof rawBody === 'object') {
        try {
          requestBody = JSON.stringify(rawBody);
        } catch (e) {
          logApiError(suppressErrorLogging, '[apiRequest] Failed to stringify body:', e);
          throw new Error('Invalid request body: cannot stringify');
        }
      } else {
        requestBody = String(rawBody);
      }
    }

    const requestConfig: RequestInit = {
      ...fetchOptions,
      ...(requestBody !== undefined && { body: requestBody }),
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(options.headers as Record<string, string> | undefined),
      },
    };

    try {
      debugLog('[apiRequest] Fetching URL:', url);
      const response = await fetch(url, requestConfig);
      debugLog('[apiRequest] Response status:', response.status, response.statusText);

      const contentType = response.headers.get('content-type');
      const isJson = contentType != null && contentType.includes('application/json');

      if (!response.ok) {
        if (!isJson) {
          const text = await response.text();
          const preview = text.substring(0, 200);
          const error = Object.assign(
            new Error(`Server returned non-JSON response (${response.status}): ${preview}${text.length > 200 ? '...' : ''}`),
            { status: response.status, isHtmlError: true },
          ) as ApiError;
          throw error;
        }
        const errorData = (await response.json().catch(() => ({}))) as Record<string, unknown>;
        const message =
          (errorData.error as string) ??
          (errorData.message as string) ??
          `HTTP ${response.status}: ${response.statusText}`;
        const error = Object.assign(new Error(message), {
          status: response.status,
          payload: errorData,
          code: errorData.code as string | undefined,
        }) as ApiError;

        // Notify the host app (mobile/desktop/web) when an authenticated
        // request gets rejected as unauthorized. The host hooks this up
        // to its auth store + router to bounce the user back to the login
        // screen instead of letting them stare at a stale POS screen
        // that silently keeps failing on every action.
        //
        // skipAuth: true is used for endpoints like /auth/login itself;
        // those 401s are "wrong PIN" feedback, not session expiry.
        if (response.status === 401 && !skipAuth) {
          try {
            onUnauthorized?.();
          } catch (handlerError) {
            logApiError(suppressErrorLogging, '[apiRequest] onUnauthorized handler threw:', handlerError);
          }
        }

        throw error;
      }

      if (
        response.status === 204 ||
        (response.status === 201 && response.headers.get('content-length') === '0')
      ) {
        return null as T;
      }

      if (rawResponse) {
        return response as unknown as T;
      }

      if (!isJson) {
        const text = await response.text();
        const preview = text.substring(0, 200);
        throw new Error(
          `Expected JSON response but got: ${contentType ?? 'unknown'}. Preview: ${preview}${text.length > 200 ? '...' : ''}`,
        );
      }

      return (await response.json()) as T;
    } catch (error) {
      const err = error as Error;
      if (err.name === 'TypeError' && (err.message.includes('fetch') || err.message.includes('Load failed'))) {
        const networkError = Object.assign(
          new Error('Network error: Unable to connect to server. Please check if the server is running.'),
          { status: 0, isNetworkError: true, originalError: error },
        ) as ApiError;
        logApiError(suppressErrorLogging, `API Error [${endpoint}]:`, networkError);
        throw networkError;
      }
      logApiError(suppressErrorLogging, `API Error [${endpoint}]:`, error);
      throw error;
    }
  }

  function handleApiError(error: unknown, fallbackMessage = 'An error occurred'): string {
    if (!error || typeof error !== 'object') {
      return fallbackMessage;
    }

    const err = error as ApiError;
    const message = err.message ?? fallbackMessage;

    if (
      err.isNetworkError ||
      err.status === 0 ||
      message.includes('fetch') ||
      message.includes('Load failed') ||
      message.includes('Network error')
    ) {
      return 'Unable to connect to server. Please check if the server is running and try again.';
    }

    if (message.includes('401') || message.includes('Unauthorized')) {
      void clearAuthToken();
      // Navigation on 401 is handled by the calling layer (platform-specific).
      // The caller should check for this string and redirect accordingly.
      return 'Session expired. Please log in again.';
    }

    if (message.includes('403') || message.includes('Forbidden')) {
      return 'You do not have permission to perform this action.';
    }

    if (message.includes('404') || message.includes('not_found') || err.status === 404) {
      return 'The requested resource was not found.';
    }

    if (
      message.includes('500') ||
      message.includes('502') ||
      message.includes('503') ||
      message.includes('504')
    ) {
      return 'Server error. Please try again later.';
    }

    return message || fallbackMessage;
  }

  return { apiRequest, handleApiError, getAuthToken, clearAuthToken };
}
